import { useState, useEffect } from 'react';
import { MapPin, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { GALLERY, GALLERY_CATS } from '../mock/data';

const INITIAL_VISIBLE = 9;
const STEP = 9;

export default function Gallery() {
  const [cat, setCat] = useState('All');
  const [visible, setVisible] = useState(INITIAL_VISIBLE);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const items = cat === 'All' ? GALLERY : GALLERY.filter((g) => g.cat === cat);
  const shown = items.slice(0, visible);

  useEffect(() => {
    setVisible(INITIAL_VISIBLE);
  }, [cat]);

  // Lightbox keyboard nav
  useEffect(() => {
    if (lightboxIndex === null) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setLightboxIndex(null);
      if (e.key === 'ArrowRight') setLightboxIndex((i) => (i + 1) % items.length);
      if (e.key === 'ArrowLeft') setLightboxIndex((i) => (i - 1 + items.length) % items.length);
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [lightboxIndex, items.length]);

  return (
    <section id="gallery" className="py-24 lg:py-32 bg-ink">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <div className="max-w-2xl">
            <div className="eyebrow">Project Gallery</div>
            <h2 className="section-title">Real projects. Real neighbors.</h2>
            <p className="section-lede">
              Every photo below is a fence or deck we built for a customer across the ArkLaTex. Tap any image to see it up close.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {GALLERY_CATS.map((c) => {
              const count = c === 'All' ? GALLERY.length : GALLERY.filter((g) => g.cat === c).length;
              return (
                <button
                  key={c}
                  onClick={() => setCat(c)}
                  className={`px-4 py-2 rounded-md text-[11px] uppercase tracking-widest font-bold border transition-colors ${
                    cat === c
                      ? 'bg-gold text-ink border-gold'
                      : 'bg-transparent text-cream/80 border-white/15 hover:border-gold hover:text-gold'
                  }`}
                >
                  {c} <span className="opacity-60 ml-1">({count})</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {shown.map((g, i) => (
            <figure
              key={g.id}
              onClick={() => setLightboxIndex(i)}
              className={`group relative rounded-md overflow-hidden bg-ink-panel border border-white/8 shadow-lg hover:shadow-2xl transition-shadow duration-500 cursor-zoom-in ${
                i % 7 === 0 ? 'sm:col-span-2 sm:row-span-1' : ''
              }`}
            >
              <div className={`overflow-hidden ${i % 7 === 0 ? 'aspect-[16/9]' : 'aspect-[4/5]'}`}>
                <img
                  src={g.image}
                  alt={g.title}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[900ms] ease-out"
                />
              </div>
              <div className="absolute inset-x-0 bottom-0 p-5 bg-gradient-to-t from-ink via-ink/40 to-transparent text-cream translate-y-2 group-hover:translate-y-0 transition-transform duration-300">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-gold font-bold">
                  <span>{g.cat}</span>
                  <span className="w-1 h-1 rounded-full bg-gold" />
                  <span className="flex items-center gap-1 text-cream/70 font-medium">
                    <MapPin className="w-3 h-3" />
                    {g.location}
                  </span>
                </div>
                <h3 className="mt-1 font-display uppercase text-xl tracking-tight">{g.title}</h3>
              </div>
            </figure>
          ))}
        </div>

        {visible < items.length && (
          <div className="mt-10 text-center">
            <button
              onClick={() => setVisible((v) => v + STEP)}
              className="btn-outline"
            >
              Load More Projects
              <span className="ml-2 opacity-60">({items.length - visible} left)</span>
            </button>
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightboxIndex !== null && items[lightboxIndex] && (
        <div className="fixed inset-0 z-[60] bg-ink/95 backdrop-blur-sm flex items-center justify-center p-4 md:p-10">
          <button
            aria-label="Close"
            onClick={() => setLightboxIndex(null)}
            className="absolute top-5 right-5 w-11 h-11 rounded-full bg-white/10 hover:bg-gold hover:text-ink text-cream flex items-center justify-center transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          <button
            aria-label="Previous"
            onClick={() => setLightboxIndex((i) => (i - 1 + items.length) % items.length)}
            className="absolute left-5 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-gold hover:text-ink text-cream flex items-center justify-center transition-colors"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            aria-label="Next"
            onClick={() => setLightboxIndex((i) => (i + 1) % items.length)}
            className="absolute right-5 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-gold hover:text-ink text-cream flex items-center justify-center transition-colors"
          >
            <ChevronRight className="w-6 h-6" />
          </button>

          <div className="max-w-6xl w-full max-h-full flex flex-col items-center gap-4">
            <img
              src={items[lightboxIndex].image}
              alt={items[lightboxIndex].title}
              className="max-w-full max-h-[75vh] object-contain rounded-md shadow-2xl"
            />
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 text-[10px] uppercase tracking-widest text-gold font-bold">
                <span>{items[lightboxIndex].cat}</span>
                <span className="w-1 h-1 rounded-full bg-gold" />
                <span className="flex items-center gap-1 text-cream/70 font-medium">
                  <MapPin className="w-3 h-3" />
                  {items[lightboxIndex].location}
                </span>
              </div>
              <h3 className="mt-1 font-display uppercase text-2xl md:text-3xl tracking-tight text-cream">
                {items[lightboxIndex].title}
              </h3>
              <div className="mt-2 text-xs text-cream/50">
                {lightboxIndex + 1} of {items.length}
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
